import { Col, Radio, Row, Form, Select, Slider, Tooltip } from "antd";
import { debounce } from "lodash";
import {
  fetchLabelByName,
  fetchLabelRankingSlidersFilters,
} from "radar/Service/RadarService";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Card, Loader, FieldSelect } from "shared/components";
import {
  RADAR_LABEL_RAKNING_FILTER,
  RADAR_LABEL_RAKNING_INDEX,
} from "shared/constants/LocalStorageConsts";
import RadioButtonRankingOptions from "../radio-button-ranking-options/RadioButtonRankingOptions";
import LabelsRankingTable from "./LabelsRankingTable";
import {
  LabelRankingIndex,
  clearLabelsRanking,
} from "explorer/reducers/rankings";
import {
  dataLayerViewedEvent,
  tagManagerEvent,
} from "event-tracker/gtm-events";

const defaults = {
  defaultForm: {
    popularity: [0, 100],
    concentration: [0, 100],
    album: [0, 10000],
    artist: [0, 10000],
    releasesLastYear: [0, 100],
    labels: [],
    genre: [],
    country: [],
  },
  defaultFilter: {
    labels: [],
    genre: [],
    country: [],
    popularityInit: 0,
    popularityEnd: 100,
    concentrationInit: 0,
    concentrationEnd: 100,
    releasesLastYearInit: 0,
    releasesLastYearEnd: 100,
    albumInit: 0,
    albumEnd: 0,
    artistInit: 0,
    artistEnd: 0,
    offset: 0,
    limit: 10,
  },
};

const initial = () => {
  const savedFilter = localStorage.getItem(RADAR_LABEL_RAKNING_FILTER);
  if (savedFilter) {
    const parsed = JSON.parse(savedFilter);
    const {
      popularityInit = 0,
      popularityEnd = 100,
      concentrationInit = 0,
      concentrationEnd = 100,
      releasesLastYearInit = 0,
      releasesLastYearEnd = 100,
      ...rest
    } = parsed;
    return {
      defaultForm: {
        ...rest,
        popularity: [popularityInit, popularityEnd],
        concentration: [concentrationInit, concentrationEnd],
        releasesLastYear: [releasesLastYearInit, releasesLastYearEnd],
      },
      defaultFilter: parsed,
    };
  }
  return {
    ...defaults,
  };
};

const LabelsRanking: React.FC<any> = (props) => {
  const savedIndex = localStorage.getItem(
    RADAR_LABEL_RAKNING_INDEX
  ) as LabelRankingIndex;
  const { history, location } = props;
  const { defaultFilter, defaultForm } = initial();
  const dispatch = useDispatch();
  const [slidersFilters, setSlidersFilters] = useState<any>();
  const [filter, setFilter] = useState<any>(defaultFilter);
  const [loadingLabels, setLoadingLabels] = useState<boolean>(false);
  const [labelOptions, setLabelOptions] = useState<any>([]);
  const [rankingIndex, setRankingIndex] = useState<LabelRankingIndex>(
    savedIndex ? savedIndex : LabelRankingIndex.Concentration
  );
  const { t } = useTranslation("radar");

  const { countries, countryOptions } = useSelector(
    (state: any) => state.countries
  );
  const { genreOptions } = useSelector((state: any) => state.genres);
  const [form] = Form.useForm();

  const onFinishFilter = () => {
    const {
      popularity,
      concentration,
      album,
      artist,
      releasesLastYear,
      ...rest
    } = form.getFieldsValue(true);
    const [popularityInit, popularityEnd] = popularity;
    const [concentrationInit, concentrationEnd] = concentration;
    const [albumInit, albumEnd] = album;
    const [artistInit, artistEnd] = artist;
    const [releasesLastYearInit, releasesLastYearEnd] = releasesLastYear;
    const saveFilter = {
      ...defaultFilter,
      ...rest,
      popularityInit,
      popularityEnd,
      concentrationInit,
      concentrationEnd,
      releasesLastYearInit,
      releasesLastYearEnd,
      albumInit,
      albumEnd,
      artistInit,
      artistEnd,
    };
    dispatch(clearLabelsRanking());
    localStorage.setItem(
      RADAR_LABEL_RAKNING_FILTER,
      JSON.stringify(saveFilter)
    );
    setFilter(saveFilter);
  };

  const getLabels = async (term: string) => {
    const cleanTerm = term.replace(/[/\\#,+()$~%.'":*?<>{}]/g, "");
    if (cleanTerm) {
      setLoadingLabels(true);
      setLabelOptions([]);
      const response: any = await fetchLabelByName(cleanTerm);
      setLabelOptions(
        response?.map((option: any) => ({ label: option, value: option }))
      );
      setLoadingLabels(false);
    }
  };

  const onDeleteClick = () => {
    dispatch(clearLabelsRanking());
    form.setFieldsValue(defaults.defaultForm);
    setFilter({
      ...defaults.defaultFilter,
      releasesLastYearEnd: slidersFilters?.albumLastYearCount,
      albumEnd: slidersFilters?.albumCount,
      artistEnd: slidersFilters?.artistsCount,
    });
  };

  const onChangeIndex = (evt: any) => {
    const saveRankingIndex = evt.target.value;
    localStorage.setItem(RADAR_LABEL_RAKNING_INDEX, saveRankingIndex);
    setRankingIndex(saveRankingIndex);
  };

  useEffect(() => {
    const getSliders = async () => {
      const response: any = await fetchLabelRankingSlidersFilters();
      setSlidersFilters(response);
      defaults.defaultForm.album = [0, response?.albumCount || 0];
      defaults.defaultForm.artist = [0, response?.artistsCount || 0];
      defaults.defaultForm.releasesLastYear = [
        0,
        response?.albumLastYearCount || 0,
      ];
      form.setFieldsValue({
        album: [0, response?.albumCount],
        artist: [0, response?.artistsCount],
        releasesLastYear: [0, response?.albumLastYearCount],
      });

      setFilter((prev: any) => ({
        ...prev,
        releasesLastYearEnd: response?.albumLastYearCount,
        albumEnd: response?.albumCount,
        artistEnd: response?.artistsCount,
      }));
    };
    getSliders();
  }, [form]);

  useEffect(() => {
    dataLayerViewedEvent({
      ...tagManagerEvent.Ranking.S4L_RANKING_LABELS,
    });
  }, []);

  return (
    <div className="radar-home">
      <Row style={{ marginTop: "16px" }}>
        <Col style={{ textAlign: "center" }}>
          <h1 className="ranking-title">{t("pages.ArtistsRanking.title")}</h1>
        </Col>
      </Row>
      <Row style={{ marginTop: "16px" }}>
        <Col style={{ textAlign: "center" }}>
          <RadioButtonRankingOptions value="label" />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col xs={24} className="conteudo-home">
          <Form
            form={form}
            initialValues={defaultForm}
            onFinish={onFinishFilter}
            className="filtro"
            layout="vertical"
          >
            <Card>
              <Row gutter={24}>
                <Col xs={5}>
                  <Form.Item
                    name="labels"
                    label={t("pages.LabelsRanking.labelFilter")}
                  >
                    <FieldSelect
                      mode="multiple"
                      filterOption={false}
                      onSearch={debounce(getLabels, 1000)}
                      notFoundContent={
                        <Loader.NotFoundLoader loading={loadingLabels} />
                      }
                      allowClear
                      dataList={labelOptions}
                      placeholder={t("pages.ArtistsRanking.selectHere")}
                      showSelectAll={false}
                    />
                  </Form.Item>
                </Col>
                <Col xs={5}>
                  <Form.Item
                    name="genre"
                    label={t("pages.ArtistsRanking.genre")}
                  >
                    <FieldSelect
                      mode="multiple"
                      allowClear
                      dataList={genreOptions}
                      placeholder={t("pages.ArtistsRanking.selectHere")}
                    />
                  </Form.Item>
                </Col>
                <Col xs={5}>
                  <Form.Item
                    noStyle
                    shouldUpdate={(prevValues, currentValues) =>
                      prevValues.country !== currentValues.country
                    }
                  >
                    {({ getFieldValue }) => {
                      const country = getFieldValue("country");
                      return (
                        <Form.Item
                          name="country"
                          label={t("pages.ArtistsRanking.country")}
                        >
                          <Select
                            mode="multiple"
                            options={countryOptions}
                            filterOption={(input, option) =>
                              (option?.label as string)
                                .toUpperCase()
                                .includes(input.toUpperCase())
                            }
                            maxTagCount={
                              country?.length > 2 ? "responsive" : 99
                            }
                            placeholder={t("pages.ArtistsRanking.selectHere")}
                          />
                        </Form.Item>
                      );
                    }}
                  </Form.Item>
                </Col>
                <Col xs={5}>
                  <Form.Item
                    name="popularity"
                    label={t("pages.LabelsRanking.popularityFilter")}
                  >
                    <Slider
                      min={0}
                      max={100}
                      marks={{
                        1: {
                          style: {
                            color: "#fff",
                          },
                          label: <strong>1</strong>,
                        },
                        100: {
                          style: {
                            color: "#fff",
                          },
                          label: <strong>100</strong>,
                        },
                      }}
                      range
                      tooltipPlacement="bottom"
                    />
                  </Form.Item>
                </Col>
                <Col xs={4}>
                  <div className="filter-button">
                    <span className="delete" onClick={onDeleteClick}>
                      {t("pages.ArtistsRanking.delete")}
                    </span>
                    <button className="apply" disabled={loadingLabels}>
                      {t("pages.ArtistsRanking.filter")}
                    </button>
                  </div>
                </Col>
                <Col xs={5}>
                  <Form.Item
                    name="concentration"
                    label={t("pages.LabelsRanking.concentrationFilter")}
                  >
                    <Slider
                      min={0}
                      max={100}
                      marks={{
                        1: {
                          style: {
                            color: "#fff",
                          },
                          label: <strong>1</strong>,
                        },
                        100: {
                          style: {
                            color: "#fff",
                          },
                          label: <strong>100</strong>,
                        },
                      }}
                      range
                      tooltipPlacement="bottom"
                    />
                  </Form.Item>
                </Col>
                <Col xs={5}>
                  <Form.Item
                    name="album"
                    label={t("pages.LabelsRanking.albunsFilter")}
                  >
                    <Slider
                      min={0}
                      max={slidersFilters?.albumCount || 1}
                      marks={{
                        1: {
                          style: {
                            color: "#fff",
                          },
                          label: <strong>1</strong>,
                        },
                        [slidersFilters?.albumCount || 100]: {
                          style: {
                            color: "#fff",
                          },
                          label: <strong>{slidersFilters?.albumCount}</strong>,
                        },
                      }}
                      step={50}
                      range
                      tooltipPlacement="bottom"
                    />
                  </Form.Item>
                </Col>
                <Col xs={5}>
                  <Form.Item
                    name="releasesLastYear"
                    label={t("pages.LabelsRanking.releasesLastYearFilter")}
                  >
                    <Slider
                      min={0}
                      max={slidersFilters?.albumLastYearCount || 1}
                      marks={{
                        1: {
                          style: {
                            color: "#fff",
                          },
                          label: <strong>1</strong>,
                        },
                        [slidersFilters?.albumLastYearCount || 100]: {
                          style: {
                            color: "#fff",
                          },
                          label: (
                            <strong>
                              {slidersFilters?.albumLastYearCount}
                            </strong>
                          ),
                        },
                      }}
                      step={10}
                      range
                      tooltipPlacement="bottom"
                    />
                  </Form.Item>
                </Col>
                <Col xs={5}>
                  <Form.Item
                    name="artist"
                    label={t("pages.LabelsRanking.artistsFilter")}
                  >
                    <Slider
                      min={0}
                      max={slidersFilters?.artistsCount || 1}
                      marks={{
                        1: {
                          style: {
                            color: "#fff",
                          },
                          label: <strong>1</strong>,
                        },
                        [slidersFilters?.artistsCount || 100]: {
                          style: {
                            color: "#fff",
                          },
                          label: (
                            <strong>{slidersFilters?.artistsCount}</strong>
                          ),
                        },
                      }}
                      step={10}
                      range
                      tooltipPlacement="bottom"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Form>
          <Row style={{ marginBottom: 16, marginTop: 16 }}>
            <Col
              xs={24}
              style={{ display: "flex", justifyContent: "flex-end" }}
            >
              <Row justify="center">
                <Radio.Group onChange={onChangeIndex} value={rankingIndex}>
                  <Radio.Button value={LabelRankingIndex.Concentration}>
                    <Tooltip
                      title={t("pages.LabelsRanking.concentrationExplaination")}
                    >
                      {t("pages.LabelsRanking.concentration")}
                    </Tooltip>
                  </Radio.Button>
                  <Radio.Button value={LabelRankingIndex.Popularity}>
                    <Tooltip
                      title={t("pages.LabelsRanking.popularityExplaination")}
                    >
                      {t("pages.LabelsRanking.popularity")}
                    </Tooltip>
                  </Radio.Button>
                  <Radio.Button value={LabelRankingIndex.Albuns}>
                    <Tooltip
                      title={t("pages.LabelsRanking.albunsExplaination")}
                    >
                      {t("pages.LabelsRanking.albuns")}
                    </Tooltip>
                  </Radio.Button>
                  <Radio.Button value={LabelRankingIndex.Artists}>
                    <Tooltip
                      title={t("pages.LabelsRanking.artistsExplaination")}
                    >
                      {t("pages.LabelsRanking.artists")}
                    </Tooltip>
                  </Radio.Button>
                  <Radio.Button value={LabelRankingIndex.Profit}>
                    <Tooltip
                      title={t("pages.LabelsRanking.currentProfitExplaination")}
                    >
                      {t("pages.LabelsRanking.currentProfit")}
                    </Tooltip>
                  </Radio.Button>
                </Radio.Group>
              </Row>
            </Col>
          </Row>
          <Row style={{ marginTop: 16 }}>
            <Col xs={24}>
              <Row>
                <Col xs={24}>
                  <LabelsRankingTable
                    filter={filter}
                    rankingIndex={rankingIndex}
                    setFilter={setFilter}
                    setLoadingLabels={setLoadingLabels}
                    loadingLabels={loadingLabels}
                    countries={countries}
                    history={history}
                    location={location}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
      </Row>
    </div>
  );
};

export default LabelsRanking;
